/**
 * «Ponme nuestro NIF y dirección en la cabecera» (Personalizar informes, F4,
 * 29-09-2026): las reglas de los datos de la cabecera que se piden y se guardan
 * desde el asistente. COPIA DEL NAVEGADOR (la usan la tarjeta «Guardar en la
 * cabecera» y el cargador de la marca de un informe del chat). La del servidor es
 * supabase/functions/_shared/marcaCabecera.ts (herramientas `build_report` y
 * `guardar_dato_cabecera`): el código es el MISMO salvo esta cabecera (guardián
 * src/__tests__/marcaCabecera.parity.test.ts). Si se separan, la tarjeta dejaría
 * guardar lo que la edge no propone, o al revés.
 */

/** Las claves que pueden salir en la cabecera (las de `visibles`, en el orden de la tabla). */
export const CAMPOS_CABECERA = ["razon_social", "nif", "direccion", "telefono", "email", "web", "linea_extra"] as const;
export type CampoCabecera = (typeof CAMPOS_CABECERA)[number];

/** La dirección se guarda en cuatro columnas; en la cabecera es UNA línea («direccion»). */
export const PARTES_DIRECCION = ["codigo_postal", "localidad", "provincia"] as const;

/** Lo que el asistente puede guardar: una columna de `company_report_branding` por dato. */
export const CAMPOS_GUARDABLES = [...CAMPOS_CABECERA, ...PARTES_DIRECCION] as const;
export type CampoGuardable = (typeof CAMPOS_GUARDABLES)[number];

/**
 * Identificadores: se repiten a la persona carácter a carácter y solo valen con
 * su «sí» (el patrón de CU7). Un NIF o un teléfono mal copiado en todos los
 * informes de una empresa es peor que preguntar una vez más.
 */
export const CAMPOS_A_CONFIRMAR: readonly CampoGuardable[] = ["nif", "telefono"];

/** Los topes de la tabla (CHECK de 20260929120000_marca_informes.sql). */
export const TOPE_CABECERA: Record<CampoGuardable, number> = {
  razon_social: 160,
  nif: 20,
  direccion: 200,
  codigo_postal: 12,
  localidad: 80,
  provincia: 80,
  telefono: 40,
  email: 160,
  web: 160,
  linea_extra: 160,
};

/**
 * Las reglas del NIF, las del cuaderno (validators.ts / dictado.ts). Se reciben
 * de fuera para que este fichero no importe nada y sea el mismo en el navegador y
 * en Deno.
 */
export interface ReglasNif {
  check: (raw: string | null | undefined) => { ok: boolean; value: string; reason?: string };
  detalle: (raw: string | null | undefined) => string | null;
  /** «be cero cuatro uno…» → «B041…» (lo que se DICTA por voz). */
  dictado: (raw: string | null | undefined) => string;
  /** La forma buena con un ejemplo (FORMATO_NIF). */
  formato: string;
}

export type MotivoDato = "campo" | "vacio" | "largo" | "formato" | "control";

export type DatoValidado =
  | { ok: true; campo: CampoGuardable; valor: string }
  | { ok: false; campo: string; motivo: MotivoDato; detalle?: string; formato?: string; valor_entendido?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEB_RE = /^(https?:\/\/)?[^\s/@]+\.[^\s@]{2,}$/i;
const TELEFONO_RE = /^\+?[0-9 ()./-]+$/;

export const esCampoGuardable = (c: unknown): c is CampoGuardable => typeof c === "string" && (CAMPOS_GUARDABLES as readonly string[]).includes(c);
export const esCampoCabecera = (c: unknown): c is CampoCabecera => typeof c === "string" && (CAMPOS_CABECERA as readonly string[]).includes(c);

/**
 * Un dato de la cabecera, limpio y comprobado con las MISMAS reglas que la tabla
 * (topes), el cuaderno (NIF con su letra de control, con el detalle de qué le
 * pasa) y la página (correo con forma de correo). Lo que no pasa no se propone
 * ni se guarda. Nunca se corrige ni se completa: se pide otra vez.
 */
export function validarDatoCabecera(campo: unknown, raw: unknown, nif: ReglasNif): DatoValidado {
  if (!esCampoGuardable(campo)) return { ok: false, campo: String(campo ?? ""), motivo: "campo" };
  const v = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!v) return { ok: false, campo, motivo: "vacio" };
  if (campo === "nif") {
    // Dictado («be cero cuatro…») o escrito: lo mismo que entiende el cuaderno (identificadorDicho).
    const entendido = nif.dictado(v) || v;
    const r = nif.check(entendido);
    if (r.ok) return { ok: true, campo, valor: r.value };
    return {
      ok: false,
      campo,
      motivo: r.reason === "control" ? "control" : "formato",
      detalle: nif.detalle(entendido) ?? undefined,
      formato: nif.formato,
      valor_entendido: r.value,
    };
  }
  if (campo === "telefono") {
    const entendido = TELEFONO_RE.test(v) ? v : nif.dictado(v) || v;
    const cifras = entendido.replace(/\D/g, "").length;
    if (!TELEFONO_RE.test(entendido) || cifras < 6 || cifras > 15) return { ok: false, campo, motivo: "formato", valor_entendido: entendido };
    return entendido.length <= TOPE_CABECERA.telefono ? { ok: true, campo, valor: entendido } : { ok: false, campo, motivo: "largo" };
  }
  if (v.length > TOPE_CABECERA[campo]) return { ok: false, campo, motivo: "largo" };
  if (campo === "email") return EMAIL_RE.test(v) ? { ok: true, campo, valor: v } : { ok: false, campo, motivo: "formato" };
  if (campo === "web") return WEB_RE.test(v) ? { ok: true, campo, valor: v } : { ok: false, campo, motivo: "formato" };
  if (campo === "codigo_postal") return /\d/.test(v) ? { ok: true, campo, valor: v } : { ok: false, campo, motivo: "formato" };
  return { ok: true, campo, valor: v };
}

/** Lo que el informe pide a la cabecera: SOLO nombres de campo (M4). */
export interface CabeceraPedida {
  /** Lo que se quiere en la cabecera de ESTE informe, además de lo que la empresa enseña siempre. */
  campos: CampoCabecera[];
  /** Lo que no se quiere en ESTE informe, aunque la empresa lo enseñe siempre («quita la dirección»). */
  quitar?: CampoCabecera[];
}

const nombres = (x: unknown): CampoCabecera[] => [...new Set((Array.isArray(x) ? x : []).filter(esCampoCabecera))];

/**
 * `cabecera` tal y como llega (del modelo o del historial) → solo nombres
 * conocidos, sin repetir; lo que se quita no se pide. null si no pide nada.
 */
export function cabeceraPedida(raw: unknown): CabeceraPedida | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const quitar = nombres(o.quitar);
  const campos = nombres(o.campos).filter((c) => !quitar.includes(c));
  if (!campos.length && !quitar.length) return null;
  return quitar.length ? { campos, quitar } : { campos };
}

/** Los nombres de `raw` que no son un campo de la cabecera (para decirlo, no para usarlos). */
export function nombresDesconocidos(raw: unknown): string[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const o = raw as Record<string, unknown>;
  return [...(Array.isArray(o.campos) ? o.campos : []), ...(Array.isArray(o.quitar) ? o.quitar : [])]
    .filter((c) => !esCampoCabecera(c))
    .map((c) => String(c));
}

const lleno = (v: unknown) => typeof v === "string" && v.trim() !== "";

/**
 * Qué campos de la cabecera tienen algo guardado (NOMBRES). La dirección cuenta
 * con cualquiera de sus cuatro columnas.
 */
export function camposGuardados(fila: Record<string, unknown> | null | undefined): CampoCabecera[] {
  if (!fila) return [];
  return CAMPOS_CABECERA.filter((c) => (c === "direccion" ? ["direccion", ...PARTES_DIRECCION].some((k) => lleno(fila[k])) : lleno(fila[c])));
}

/** Lo pedido que no está guardado: lo que hay que preguntar (NOMBRES). */
export function faltanDe(pedida: CabeceraPedida | null, guardados: readonly string[]): CampoCabecera[] {
  if (!pedida) return [];
  return pedida.campos.filter((c) => !guardados.includes(c));
}

/**
 * Las líneas que lleva la cabecera de ESTE informe: lo que la empresa enseña
 * siempre, más lo pedido, menos lo quitado.
 */
export function camposDeLaCabecera(visibles: readonly string[] | null | undefined, pedida: CabeceraPedida | null): CampoCabecera[] {
  const quitar = new Set<string>(pedida?.quitar ?? []);
  const set = new Set<string>([...(visibles ?? []), ...(pedida?.campos ?? [])]);
  return CAMPOS_CABECERA.filter((c) => set.has(c) && !quitar.has(c));
}

/** La tarjeta `marca_cambio` tal y como la pinta el chat: un dato, ya comprobado. */
export interface TarjetaMarca {
  campo: CampoGuardable;
  valor: string;
}

/**
 * ¿Se puede guardar esta tarjeta? La tarjeta viaja por la red y se guarda en el
 * historial (con el NIF tapado, E11): se vuelve a comprobar ENTERA. Una tapada o
 * trucada no pasa (null).
 */
export function leerTarjetaMarca(item: unknown, nif: ReglasNif): TarjetaMarca | null {
  if (!item || typeof item !== "object") return null;
  const o = item as Record<string, unknown>;
  if (o.v !== 1 || typeof o.valor !== "string") return null;
  const r = validarDatoCabecera(o.campo, o.valor, nif);
  // Lo que se guarda es EXACTAMENTE lo que se enseñó: si la regla lo cambiaría, no vale.
  if (!r.ok || r.valor !== o.valor) return null;
  return { campo: r.campo, valor: r.valor };
}
